import { generatePath } from 'react-router-dom';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { showSuccess } from '@components/toaster';
import { useThrottledMutation as useMutation } from '@hooks/use-throttled-mutation';
import { useScopedTranslate } from '@locale/translate';

import { useInvalidateFavorites } from 'api/favorites/hooks';
import { handleError } from 'api/query-client';
import { routePaths } from 'ui/authenticated/navigation';

import {
  createFormTemplate,
  createFormTemplateImage,
  deleteFormTemplate,
  duplicateFormTemplate,
  loadForm,
  loadFormTemplate,
  loadFormTemplateJourneys,
  loadFormTemplates,
  moveFormTemplate,
  saveFormTemplate,
} from './requests';

export const useFormTemplates = (queryString?: string, disabled?: boolean) => {
  return useQuery({
    enabled: !!queryString && !disabled,
    queryKey: ['form-templates', queryString ?? ''],
    queryFn: () => loadFormTemplates(queryString),
    keepPreviousData: true,
  });
};

export const useForm = (shortId: string) => {
  return useQuery({
    queryKey: ['form', shortId],
    queryFn: () => loadForm(shortId),
  });
};

export const useFormTemplate = (shortId: string) => {
  return useQuery({
    queryKey: ['form-templates', shortId],
    queryFn: () => loadFormTemplate(shortId),
  });
};

export const useEditTemplate = () => {
  const { t } = useScopedTranslate(locale);
  const { invalidateFavorites } = useInvalidateFavorites();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: saveFormTemplate,
    onSuccess: async () => {
      await queryClient.invalidateQueries(['form-templates']);
      showSuccess(t('formTemplateUpdated'));
      invalidateFavorites();
    },
    onError: () => {
      handleError(t('formTemplateUpdateFailed'));
    },
  });
};

export const useCreateFormTemplate = () => {
  const { t } = useScopedTranslate(locale);

  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createFormTemplate,
    onSuccess: async () => {
      await queryClient.invalidateQueries(['form-templates']);
      showSuccess(t('formTemplateCreated'));
    },
    onError: () => {
      handleError(t('formTemplateCreateFailed'));
    },
  });
};

export const useDeleteTemplate = (shortId: string) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateFavorites } = useInvalidateFavorites();

  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => deleteFormTemplate(shortId),
    onSuccess: async () => {
      await queryClient.invalidateQueries(['form-templates']);
      showSuccess(t('formTemplateDeleted'));
      invalidateFavorites();
    },
    onError: () => {
      handleError(t('formTemplateDeletedFailed'));
    },
  });
};

export const useDuplicateFormTemplate = (templateId: string) => {
  const { t } = useScopedTranslate(locale);
  const queryClient = useQueryClient();

  return useMutation((data: { name: string }) => duplicateFormTemplate(templateId, data), {
    onSuccess: (responseData) => {
      const duplicatedJourneyPath = generatePath(routePaths.workspaceFormTemplatesEdit, {
        workspaceId: responseData.workspace,
        shortId: responseData.shortId,
        '*': '',
      });
      queryClient.invalidateQueries(['form-templates']);
      showSuccess(responseData.name, {
        subtitle: t('formTemplateDuplicated'),
        url: duplicatedJourneyPath,
      });
    },
    onError: () => {
      handleError(t('formTemplateDuplicatedFailed'));
    },
  });
};

export const useMoveFormTemplate = (shortId: string) => {
  const { t } = useScopedTranslate(locale);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (workspaceId: string) => moveFormTemplate(shortId, workspaceId),
    onSuccess: () => {
      showSuccess(t('formTemplateMoved'));
      queryClient.invalidateQueries(['form-templates']);
    },
    onError: () => {
      handleError(t('formTemplateMovedFailed'));
    },
  });
};

export const useFormTemplateJourneys = (shortId: string) => {
  return useQuery({
    queryKey: ['form-template-journeys', shortId],
    queryFn: () => loadFormTemplateJourneys(shortId),
  });
};

export const useCreateFormTemplateImage = () => {
  const { t } = useScopedTranslate(locale);

  return useMutation({
    mutationFn: createFormTemplateImage,
    onError: () => {
      handleError(t('imageSaveFailed'));
    },
  });
};

export const locale = {
  en: {
    formTemplateUpdated: 'Form template updated',
    formTemplateUpdateFailed: 'Failed to update form template',
    formTemplateCreated: 'Form template created',
    formTemplateCreateFailed: 'Failed to create form template',
    formTemplateDeleted: 'Form successfully deleted',
    formTemplateDeletedFailed: 'Failed to delete form',
    formTemplateDuplicated: 'Form template duplicated',
    formTemplateDuplicatedFailed: 'Failed to duplicate form template',
    formTemplateMoved: 'Form template moved',
    formTemplateMovedFailed: 'Failed to move form template',
    imageSaveFailed: 'Failed to save image',
  },
};
